import { Component, Input, OnInit } from '@angular/core';
import { GoogleService } from '../../../services/google.service';

interface DriveItem {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime?: string;
  webViewLink?: string;
  iconLink?: string;
}

@Component({
  selector: 'app-client-files',
  templateUrl: './client-files.component.html',
  styleUrls: ['./client-files.component.css']
})
export class ClientFilesComponent implements OnInit {
  @Input() clientCode = 'QA';
  @Input() clientName = 'Client';
  loading = false;
  errorMsg = '';
  pathStack: { id: string; name: string }[] = [];
  items: DriveItem[] = [];
  nextPageToken = '';

  constructor(private google: GoogleService) { }

  ngOnInit(): void {
    this.loadClientRoot();
  }

  private setError(msg: string) {
    this.errorMsg = msg || '';
    this.loading = false;
  }

  loadClientRoot(): void {
    this.loading = true;
    this.errorMsg = '';
    this.items = [];
    this.pathStack = [];
    this.nextPageToken = '';

    this.google.getClientRoot({ code: this.clientCode, name: this.clientName }).subscribe({
      next: (root) => {
        if (!root) {
          this.setError('Client folder not found (shared with me).');
          return;
        }
        this.pathStack = [{ id: root.id, name: root.name }];
        this.loadChildren(root.id);
      },
      error: (e) => this.setError(e?.message || 'Failed to resolve client root.')
    });
  }

  loadChildren(folderId: string, pageToken = ''): void {
    this.loading = true;
    this.errorMsg = '';
    this.google.listFolderChildren(folderId, 100, pageToken).subscribe({
      next: (res) => {
        if (pageToken) {
          this.items = [...this.items, ...(res?.files ?? [])];
        } else {
          this.items = res?.files ?? [];
        }
        this.nextPageToken = res?.nextPageToken ?? '';
        this.loading = false;
      },
      error: (e) => this.setError(e?.message || 'Failed to list folder children.')
    });
  }

  enterFolder(item: DriveItem): void {
    this.items = [];
    this.nextPageToken = '';
    this.pathStack.push({ id: item.id, name: item.name });
    this.loadChildren(item.id);
  }

  jumpTo(index: number): void {
    this.pathStack = this.pathStack.slice(0, index + 1);
    const target = this.pathStack[this.pathStack.length - 1];
    this.items = [];
    this.nextPageToken = '';
    this.loadChildren(target.id);
  }

  loadMore(): void {
    const current = this.pathStack[this.pathStack.length - 1];
    if (current && this.nextPageToken) {
      this.loadChildren(current.id, this.nextPageToken);
    }
  }

  open(item: DriveItem): void {
    if (item.webViewLink) {
      window.open(item.webViewLink, '_blank', 'noopener');
    }
  }

  isFolder(item: DriveItem): boolean {
    return item.mimeType === 'application/vnd.google-apps.folder';
  }

  refresh(): void {
    if (!this.pathStack.length) {
      this.loadClientRoot();
      return;
    }
    const current = this.pathStack[this.pathStack.length - 1];
    this.loadChildren(current.id);
  }
}
